import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X, User, LogOut, LayoutDashboard, Settings, Shield, ShieldCheck, Building2, MessageCircle, Bell, Globe, Search } from 'lucide-react';
import axios from 'axios';
import { useAuth, API } from '../App';
import { getLanguage, setLanguage, t } from '../i18n';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const Header = () => {
  const { user, logout, features } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [unreadNotifs, setUnreadNotifs] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [notifOpen, setNotifOpen] = useState(false);
  const [lang, setLang] = useState(getLanguage());
  const [isOrgAdmin, setIsOrgAdmin] = useState(false);

  const fetchCounts = useCallback(async () => {
    if (!user) return;
    try {
      const [msgRes, notifRes] = await Promise.all([
        axios.get(`${API}/messages/unread-total`, { withCredentials: true }),
        axios.get(`${API}/notifications/unread-count`, { withCredentials: true })
      ]);
      setUnreadMessages(msgRes.data.unread_count || 0);
      setUnreadNotifs(notifRes.data.unread_count || 0);
    } catch { /* silent */ }
  }, [user]);

  // Detect if user is admin of at least one organization (for conditional nav link)
  useEffect(() => {
    if (!user) { setIsOrgAdmin(false); return; }
    let alive = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/organizations/my`, { withCredentials: true });
        if (alive) setIsOrgAdmin((res.data || []).length > 0);
      } catch { if (alive) setIsOrgAdmin(false); }
    })();
    return () => { alive = false; };
  }, [user]);

  useEffect(() => {
    fetchCounts();
    if (!user) return;
    const interval = setInterval(fetchCounts, 15000);
    return () => clearInterval(interval);
  }, [fetchCounts, user]);

  const fetchNotifications = async () => {
    try {
      const res = await axios.get(`${API}/notifications?limit=10`, { withCredentials: true });
      setNotifications(res.data);
    } catch { /* silent */ }
  };

  const openNotifPanel = async () => {
    if (!notifOpen) {
      await fetchNotifications();
      // Mark all as read
      try {
        await axios.patch(`${API}/notifications/mark-read`, {}, { withCredentials: true });
        setUnreadNotifs(0);
      } catch { /* silent */ }
    }
    setNotifOpen(!notifOpen);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const formatTimeAgo = (dateStr) => {
    const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
    if (diff < 60) return 'maintenant';
    if (diff < 3600) return `${Math.floor(diff / 60)}min`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return `${Math.floor(diff / 86400)}j`;
  };

  const notifIcon = (type) => {
    switch (type) {
      case 'new_message': return <MessageCircle className="w-4 h-4 text-accent" />;
      case 'new_conversation': return <MessageCircle className="w-4 h-4 text-primary" />;
      case 'owner_reply': return <MessageCircle className="w-4 h-4 text-secondary" />;
      default: return <Bell className="w-4 h-4 text-charcoal/60" />;
    }
  };

  return (
    <header className="sticky top-0 z-50 bg-background/90 backdrop-blur-md border-b-2 border-charcoal">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="header-logo">
            <div className="bg-primary w-10 h-10 rounded-full border-2 border-charcoal flex items-center justify-center shadow-brutal">
              <span className="font-heading font-bold text-charcoal">T</span>
            </div>
            <span className="font-heading font-bold text-xl text-charcoal">Tagora</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-6">
            <Link
              to="/business"
              data-testid="nav-business"
              className="text-charcoal/70 hover:text-charcoal font-medium transition-colors flex items-center gap-2"
            >
              <Building2 className="w-4 h-4" />
              {t('nav.business')}
            </Link>
            <Link
              to="/label"
              data-testid="nav-label"
              className="text-charcoal/70 hover:text-charcoal font-medium transition-colors flex items-center gap-2"
            >
              <ShieldCheck className="w-4 h-4" />
              Standard
            </Link>

            {/* Verify code (loupe) - visible to everyone, anywhere on the app */}
            <Link
              to="/verify"
              data-testid="nav-verify"
              title="Verifier un code Tagora"
              className="text-charcoal/70 hover:text-charcoal transition-colors p-2 rounded-full hover:bg-surface-alt"
            >
              <Search className="w-5 h-5" />
            </Link>

            {user ? (
              <>
                <Link
                  to="/dashboard"
                  data-testid="nav-dashboard"
                  className="text-charcoal/70 hover:text-charcoal font-medium transition-colors flex items-center gap-2"
                >
                  <LayoutDashboard className="w-4 h-4" />
                  Dashboard
                </Link>

                {isOrgAdmin && (
                  <Link
                    to="/organisations"
                    data-testid="nav-organisations"
                    className="text-charcoal/70 hover:text-charcoal font-medium transition-colors flex items-center gap-2"
                  >
                    <Building2 className="w-4 h-4" />
                    Organisations
                  </Link>
                )}

                {/* Messages Icon */}
                {features?.messaging_enabled !== false && (
                <Link
                  to="/messages"
                  data-testid="nav-messages"
                  className="relative text-charcoal/70 hover:text-charcoal transition-colors p-2"
                  title="Messages"
                >
                  <MessageCircle className="w-5 h-5" />
                  {unreadMessages > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 bg-danger text-white text-[10px] font-bold min-w-[18px] h-[18px] rounded-full flex items-center justify-center border-2 border-background" data-testid="messages-badge">
                      {unreadMessages > 9 ? '9+' : unreadMessages}
                    </span>
                  )}
                </Link>
                )}

                {/* Notifications Icon */}
                <div className="relative">
                  <button
                    onClick={openNotifPanel}
                    data-testid="nav-notifications"
                    className="relative text-charcoal/70 hover:text-charcoal transition-colors p-2"
                    title="Notifications"
                  >
                    <Bell className="w-5 h-5" />
                    {unreadNotifs > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 bg-danger text-white text-[10px] font-bold min-w-[18px] h-[18px] rounded-full flex items-center justify-center border-2 border-background" data-testid="notif-badge">
                        {unreadNotifs > 9 ? '9+' : unreadNotifs}
                      </span>
                    )}
                  </button>

                  {/* Notification Dropdown */}
                  {notifOpen && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setNotifOpen(false)} />
                      <div className="absolute right-0 top-full mt-2 w-80 bg-white border-2 border-charcoal rounded-2xl shadow-brutal-xl z-50 max-h-[400px] overflow-hidden" data-testid="notif-dropdown">
                        <div className="p-3 border-b-2 border-charcoal/10 flex items-center justify-between">
                          <h3 className="font-bold text-charcoal text-sm">Notifications</h3>
                          <Link to="/messages" onClick={() => setNotifOpen(false)} className="text-xs text-primary font-bold hover:underline">
                            Voir tous les messages
                          </Link>
                        </div>
                        <div className="overflow-y-auto max-h-[340px]">
                          {notifications.length === 0 ? (
                            <div className="p-6 text-center">
                              <Bell className="w-8 h-8 text-charcoal/20 mx-auto mb-2" />
                              <p className="text-sm text-charcoal/50">Aucune notification</p>
                            </div>
                          ) : (
                            notifications.map((n) => (
                              <Link
                                key={n.notification_id}
                                to={n.link || '/messages'}
                                onClick={() => setNotifOpen(false)}
                                className={`block px-4 py-3 hover:bg-primary/5 transition-colors border-b border-charcoal/5 ${!n.is_read ? 'bg-primary/5' : ''}`}
                                data-testid={`notif-item-${n.notification_id}`}
                              >
                                <div className="flex items-start gap-3">
                                  <div className="mt-0.5">{notifIcon(n.type)}</div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-charcoal truncate">{n.title}</p>
                                    <p className="text-xs text-charcoal/60 line-clamp-2">{n.body}</p>
                                    <p className="text-[10px] text-charcoal/40 mt-1">{formatTimeAgo(n.created_at)}</p>
                                  </div>
                                </div>
                              </Link>
                            ))
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* Language Switch */}
                <button
                  onClick={() => { const newLang = lang === 'fr' ? 'en' : 'fr'; setLanguage(newLang); setLang(newLang); window.location.reload(); }}
                  data-testid="lang-switch-desktop"
                  className="text-xs font-bold bg-white border-2 border-charcoal/20 rounded-full px-3 py-1.5 hover:border-charcoal transition-colors"
                >
                  {lang === 'fr' ? 'EN' : 'FR'}
                </button>
                
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      data-testid="user-menu-btn"
                      className="flex items-center gap-2 bg-white border-2 border-charcoal rounded-full px-4 py-2 shadow-brutal hover:shadow-brutal-lg transition-all"
                    >
                      {user.picture ? (
                        <img src={user.picture} alt={user.name} className="w-6 h-6 rounded-full" />
                      ) : (
                        <User className="w-5 h-5 text-charcoal" />
                      )}
                      <span className="font-bold text-charcoal text-sm">{user.name?.split(' ')[0]}</span>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48 bg-white border-2 border-charcoal rounded-xl shadow-brutal">
                    <DropdownMenuItem asChild>
                      <Link to="/dashboard" className="flex items-center gap-2 cursor-pointer">
                        <LayoutDashboard className="w-4 h-4" />
                        Dashboard
                      </Link>
                    </DropdownMenuItem>
                    {isOrgAdmin && (
                      <DropdownMenuItem asChild>
                        <Link to="/organisations" className="flex items-center gap-2 cursor-pointer" data-testid="dropdown-nav-organisations">
                          <Building2 className="w-4 h-4" />
                          Organisations
                        </Link>
                      </DropdownMenuItem>
                    )}
                    {features?.messaging_enabled !== false && (
                    <DropdownMenuItem asChild>
                      <Link to="/messages" className="flex items-center gap-2 cursor-pointer">
                        <MessageCircle className="w-4 h-4" />
                        Messages
                        {unreadMessages > 0 && (
                          <span className="ml-auto bg-danger text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">{unreadMessages}</span>
                        )}
                      </Link>
                    </DropdownMenuItem>
                    )}
                    <DropdownMenuItem asChild>
                      <Link to="/account" className="flex items-center gap-2 cursor-pointer">
                        <Settings className="w-4 h-4" />
                        Mon compte
                      </Link>
                    </DropdownMenuItem>
                    {user.role === 'admin' && (
                      <DropdownMenuItem asChild>
                        <Link to="/admin" className="flex items-center gap-2 cursor-pointer" data-testid="nav-admin">
                          <Shield className="w-4 h-4" />
                          Backoffice
                        </Link>
                      </DropdownMenuItem>
                    )}
                    {user.role === 'partner' && (
                      <DropdownMenuItem asChild>
                        <Link to="/partners/dashboard" className="flex items-center gap-2 cursor-pointer" data-testid="nav-partner-dashboard">
                          <Shield className="w-4 h-4" />
                          Espace partenaire
                        </Link>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => { const newLang = lang === 'fr' ? 'en' : 'fr'; setLanguage(newLang); setLang(newLang); window.location.reload(); }}
                      className="flex items-center gap-2 cursor-pointer"
                      data-testid="lang-switch-dropdown"
                    >
                      <Globe className="w-4 h-4" />
                      {lang === 'fr' ? 'English' : 'Francais'}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={handleLogout}
                      data-testid="logout-btn"
                      className="flex items-center gap-2 cursor-pointer text-danger focus:text-danger"
                    >
                      <LogOut className="w-4 h-4" />
                      Deconnexion
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <div className="flex items-center gap-3">
                <button
                  onClick={() => { const newLang = lang === 'fr' ? 'en' : 'fr'; setLanguage(newLang); setLang(newLang); window.location.reload(); }}
                  data-testid="lang-switch"
                  className="text-xs font-bold bg-white border-2 border-charcoal/20 rounded-full px-3 py-1.5 hover:border-charcoal transition-colors"
                >
                  {lang === 'fr' ? 'EN' : 'FR'}
                </button>
                <Link
                  to="/login"
                  data-testid="nav-login"
                  className="text-charcoal font-bold hover:text-primary transition-colors"
                >
                  {t('nav.login')}
                </Link>
                <Link
                  to="/register"
                  data-testid="nav-register"
                  className="btn-primary text-sm px-4 py-2"
                >
                  {t('nav.register')}
                </Link>
              </div>
            )}
          </nav>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center gap-2">
            {user && (
              <>
                {features?.messaging_enabled !== false && (
                <Link to="/messages" className="relative p-2" data-testid="mobile-nav-messages">
                  <MessageCircle className="w-5 h-5 text-charcoal" />
                  {unreadMessages > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 bg-danger text-white text-[10px] font-bold min-w-[16px] h-[16px] rounded-full flex items-center justify-center">
                      {unreadMessages > 9 ? '9+' : unreadMessages}
                    </span>
                  )}
                </Link>
                )}
                <button onClick={openNotifPanel} className="relative p-2" data-testid="mobile-nav-notif">
                  <Bell className="w-5 h-5 text-charcoal" />
                  {unreadNotifs > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 bg-danger text-white text-[10px] font-bold min-w-[16px] h-[16px] rounded-full flex items-center justify-center">
                      {unreadNotifs > 9 ? '9+' : unreadNotifs}
                    </span>
                  )}
                </button>
              </>
            )}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 rounded-full border-2 border-charcoal"
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Notification Dropdown */}
        {notifOpen && (
          <>
            <div className="fixed inset-0 z-40 md:hidden" onClick={() => setNotifOpen(false)} />
            <div className="md:hidden absolute left-4 right-4 top-16 bg-white border-2 border-charcoal rounded-2xl shadow-brutal-xl z-50 max-h-[60vh] overflow-hidden" data-testid="mobile-notif-dropdown">
              <div className="p-3 border-b-2 border-charcoal/10 flex items-center justify-between">
                <h3 className="font-bold text-charcoal text-sm">Notifications</h3>
                <button onClick={() => setNotifOpen(false)} className="text-charcoal/40 hover:text-charcoal">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="overflow-y-auto max-h-[50vh]">
                {notifications.length === 0 ? (
                  <div className="p-6 text-center">
                    <Bell className="w-8 h-8 text-charcoal/20 mx-auto mb-2" />
                    <p className="text-sm text-charcoal/50">Aucune notification</p>
                  </div>
                ) : (
                  notifications.map((n) => (
                    <Link
                      key={n.notification_id}
                      to={n.link || '/messages'}
                      onClick={() => setNotifOpen(false)}
                      className={`block px-4 py-3 hover:bg-primary/5 transition-colors border-b border-charcoal/5 ${!n.is_read ? 'bg-primary/5' : ''}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5">{notifIcon(n.type)}</div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-charcoal truncate">{n.title}</p>
                          <p className="text-xs text-charcoal/60 line-clamp-2">{n.body}</p>
                          <p className="text-[10px] text-charcoal/40 mt-1">{formatTimeAgo(n.created_at)}</p>
                        </div>
                      </div>
                    </Link>
                  ))
                )}
              </div>
            </div>
          </>
        )}

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t-2 border-charcoal py-4 space-y-4">
            {/* Language switch mobile */}
            <button
              onClick={() => { const newLang = lang === 'fr' ? 'en' : 'fr'; setLanguage(newLang); setLang(newLang); window.location.reload(); }}
              data-testid="mobile-lang-switch"
              className="flex items-center gap-2 text-charcoal/70 hover:text-charcoal font-medium py-2"
            >
              <Globe className="w-4 h-4" />
              {lang === 'fr' ? 'English' : 'Francais'}
            </button>
            <Link
              to="/business"
              onClick={() => setMobileMenuOpen(false)}
              className="block text-charcoal/70 hover:text-charcoal font-medium py-2"
            >
              {t('nav.business')}
            </Link>
            <Link
              to="/label"
              onClick={() => setMobileMenuOpen(false)}
              data-testid="mobile-nav-label"
              className="flex items-center gap-2 text-charcoal/70 hover:text-charcoal font-medium py-2"
            >
              <ShieldCheck className="w-4 h-4" /> Standard
            </Link>
            <Link
              to="/verify"
              onClick={() => setMobileMenuOpen(false)}
              data-testid="mobile-nav-verify"
              className="flex items-center gap-2 text-charcoal/70 hover:text-charcoal font-medium py-2"
            >
              <Search className="w-4 h-4" /> Verifier un code
            </Link>
            {user ? (
              <>
                <Link
                  to="/dashboard"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block text-charcoal/70 hover:text-charcoal font-medium py-2"
                >
                  {t('nav.dashboard')}
                </Link>
                {isOrgAdmin && (
                  <Link
                    to="/organisations"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-2 text-charcoal/70 hover:text-charcoal font-medium py-2"
                    data-testid="mobile-nav-organisations"
                  >
                    <Building2 className="w-4 h-4" /> Organisations
                  </Link>
                )}
                {features?.messaging_enabled !== false && (
                <Link
                  to="/messages"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-2 text-charcoal/70 hover:text-charcoal font-medium py-2"
                >
                  {t('nav.messages')}
                  {unreadMessages > 0 && (
                    <span className="bg-danger text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">{unreadMessages}</span>
                  )}
                </Link>
                )}
                <Link
                  to="/account"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block text-charcoal/70 hover:text-charcoal font-medium py-2"
                >
                  {t('nav.account')}
                </Link>
                {user.role === 'admin' && (
                  <Link
                    to="/admin"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block text-charcoal/70 hover:text-charcoal font-medium py-2"
                    data-testid="mobile-nav-admin"
                  >
                    {t('nav.admin')}
                  </Link>
                )}
                <button
                  onClick={() => {
                    handleLogout();
                    setMobileMenuOpen(false);
                  }}
                  className="block text-danger font-medium py-2"
                >
                  {t('nav.logout')}
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block text-charcoal/70 hover:text-charcoal font-medium py-2"
                >
                  {t('nav.login')}
                </Link>
                <Link
                  to="/register"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block btn-primary text-center"
                >
                  {t('nav.register')}
                </Link>
              </>
            )}
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
